import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Mail, MessageCircle, Book, HelpCircle } from 'lucide-react'
import Navigation from '../components/Navigation'
import Footer from '../components/Footer'

const HelpCenter = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null)

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index)
  }

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          question: 'How do I set up my FlowBeatIQ sensor?',
          answer: 'Setting up your sensor is simple: 1) Plug the sensor into a standard power outlet in your business location. 2) Connect to the sensor\'s WiFi network from your phone or laptop (network name starts with "FlowBeatIQ-"). 3) Follow the on-screen prompts to connect the sensor to your business WiFi. 4) The sensor will appear in your dashboard within 5 minutes. See our <a href="/setup-guide" class="text-blue-600 hover:underline">detailed setup guide</a> for step-by-step instructions.'
        },
        {
          question: 'Do I need technical skills to install the sensor?',
          answer: 'No technical skills required! If you can connect to WiFi on your phone, you can set up FlowBeatIQ. The entire process takes under 10 minutes. If you provide your WiFi details at checkout, we can pre-configure your sensor so it\'s literally plug-and-play.'
        },
        {
          question: 'How long does it take to see data?',
          answer: 'You\'ll see your first data points within 5-10 minutes of plugging in the sensor. However, the system takes 7 days to learn your unique traffic patterns for accurate activity level calculations. During this learning period, you\'ll see conservative activity levels (3-7 range) and a "Learning your traffic patterns" indicator.'
        },
        {
          question: 'What is the free trial period?',
          answer: 'We offer a 14-day free trial with full access to all features and one sensor device. You can cancel anytime during the trial without being charged. If you continue after the trial, your subscription will begin automatically.'
        }
      ]
    },
    {
      category: 'How It Works',
      questions: [
        {
          question: 'How does FlowBeatIQ detect visitors?',
          answer: 'Our sensors detect anonymous WiFi probe requests from smartphones and other mobile devices. Most smartphones continuously send out these signals to search for known WiFi networks. We count these signals to estimate foot traffic without collecting any personal information, device contents, or identifiable data.'
        },
        {
          question: 'How accurate is the visitor counting?',
          answer: 'FlowBeatIQ achieves 80-90% consistency in most environments, which is excellent for spotting trends and making business decisions. The system becomes more accurate over time as it learns your specific traffic patterns during the 7-day learning period.'
        },
        {
          question: 'What if customers turn off WiFi on their phones?',
          answer: 'While some people disable WiFi, the vast majority keep it enabled. Even if some customers have WiFi turned off, the overall trends and patterns remain statistically reliable for making informed business decisions.'
        },
        {
          question: 'What is the Activity Level (1-10 scale)?',
          answer: 'Instead of showing raw visitor counts, we use a simple 1-10 scale: 1-2 (Empty), 3-4 (Quiet), 5-6 (Average), 7-8 (Busy), 9-10 (Packed). This makes it easier to understand patterns quickly without over-focusing on exact numbers, which naturally fluctuate.'
        },
        {
          question: 'How do you calculate "Today" vs "Typical Day"?',
          answer: 'We only count activity during your business hours. "Today" shows your current or recent activity. "Typical Day" compares your current traffic to the average for the same weekday over the past 4 weeks, adjusted for how far into the day you are.'
        }
      ]
    },
    {
      category: 'Privacy & Data',
      questions: [
        {
          question: 'What data do you collect?',
          answer: 'We collect only anonymous WiFi signal information (MAC addresses that are immediately hashed) to count visitors. We do NOT collect names, locations, browsing history, personal information, or any content from devices. See our <a href="/privacy" class="text-blue-600 hover:underline">Privacy Policy</a> for complete details.'
        },
        {
          question: 'Is FlowBeatIQ GDPR and privacy-compliant?',
          answer: 'Yes! FlowBeatIQ is designed with privacy at its core. We use hashed MAC addresses and don\'t track individuals or collect personal data. Our approach is compliant with GDPR, CCPA, and other major privacy regulations. See our <a href="/dpa" class="text-blue-600 hover:underline">Data Processing Addendum</a> for more information.'
        },
        {
          question: 'Do you use cameras?',
          answer: 'No. FlowBeatIQ uses WiFi sensing only—no cameras, no facial recognition, no visual surveillance. Your customers\' privacy is fully protected.'
        },
        {
          question: 'Can you identify individual customers?',
          answer: 'No. We cannot and do not identify individual people. The MAC addresses we detect are immediately hashed (converted to irreversible codes) and cannot be traced back to specific individuals or devices.'
        }
      ]
    },
    {
      category: 'Features & Usage',
      questions: [
        {
          question: 'Can I manage multiple locations?',
          answer: 'Yes! You can add as many sensors as you need and manage all your locations from a single dashboard. Each additional location is $79/month. You\'ll see consolidated analytics across all locations as well as individual location breakdowns.'
        },
        {
          question: 'What happens if my sensor goes offline?',
          answer: 'If your sensor loses WiFi connection, it will automatically attempt to reconnect. The dashboard will show "No Recent Data" if the sensor hasn\'t reported in over 10 minutes. Once reconnected, the sensor will resume normal operation. Your historical data is safe and will not be affected.'
        },
        {
          question: 'Can I export my data?',
          answer: 'Yes! You can export your analytics data in CSV or PDF formats directly from the dashboard. This includes visitor counts, activity levels, peak hours, and comparison metrics for any date range.'
        },
        {
          question: 'Do you provide email alerts?',
          answer: 'Coming soon! We\'re adding email notifications for sensor offline alerts, anomaly detection, and weekly/monthly digest reports. You\'ll be able to configure your notification preferences in your account settings.'
        },
        {
          question: 'What is the "Busiest Hour" feature?',
          answer: 'The Busiest Hour feature automatically identifies which hour of the day had the highest average activity. This helps you understand when to schedule more staff, plan promotions, or optimize your operations.'
        }
      ]
    },
    {
      category: 'Billing & Subscription',
      questions: [
        {
          question: 'How much does FlowBeatIQ cost?',
          answer: 'FlowBeatIQ is $99/month for one location with one sensor device. Additional locations are $79/month each. All plans include a 14-day free trial, full dashboard access, and unlimited historical data. No contracts—cancel anytime.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) processed securely through Stripe. We do not store your payment information on our servers.'
        },
        {
          question: 'Can I cancel anytime?',
          answer: 'Yes! You can cancel your subscription at any time through your account settings or by contacting support@flowbeatiq.com. Your cancellation will take effect at the end of your current billing period, and you\'ll retain access until then.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'We offer a 30-day money-back guarantee. If you\'re not satisfied with FlowBeatIQ within the first 30 days, contact us at support@flowbeatiq.com and we\'ll refund your payment and cover return shipping for the sensor—no questions asked.'
        },
        {
          question: 'How do I upgrade to multiple locations?',
          answer: 'You can add additional sensors and locations directly from your dashboard under Settings > Sensors. Each additional location is $79/month and will be pro-rated for your current billing period.'
        }
      ]
    },
    {
      category: 'Technical Support',
      questions: [
        {
          question: 'What if my sensor isn\'t showing data?',
          answer: 'First, check that the sensor is plugged in and the LED indicator is on. Ensure your WiFi network is working. If the sensor still doesn\'t appear in your dashboard after 15 minutes, try unplugging it for 30 seconds and plugging it back in. If issues persist, contact support@flowbeatiq.com with your sensor ID.'
        },
        {
          question: 'How do I reset my sensor?',
          answer: 'To factory reset your sensor, press and hold the reset button (small hole on the device) for 10 seconds using a paperclip. The LED will blink rapidly to confirm the reset. You\'ll need to set up the sensor again through the WiFi configuration portal.'
        },
        {
          question: 'Can I move my sensor to a different location?',
          answer: 'Yes! You can unplug your sensor and move it anywhere within the same business location. If you\'re moving it to a completely different business address, update the location settings in your dashboard under Settings > Sensors to ensure accurate analytics.'
        },
        {
          question: 'What are the WiFi requirements?',
          answer: 'Your sensor needs a standard 2.4GHz WiFi connection (5GHz is not supported). The WiFi network should have internet access and allow outbound HTTPS connections. Corporate networks with strict firewall rules may require whitelisting our servers—contact support for details.'
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container-padding py-12">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
            <HelpCircle className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-xl text-gray-600">
            Find answers to common questions about FlowBeatIQ
          </p>
        </div>

        {/* Quick Contact Cards */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <a href="mailto:support@flowbeatiq.com" className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
            <Mail className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Email Support</h3>
            <p className="text-sm text-gray-600 mb-2">Get help from our team</p>
            <p className="text-sm text-blue-600 font-medium">support@flowbeatiq.com</p>
          </a>

          <Link to="/setup-guide" className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
            <Book className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Setup Guide</h3>
            <p className="text-sm text-gray-600 mb-2">Step-by-step installation</p>
            <p className="text-sm text-green-600 font-medium">View guide →</p>
          </Link>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <MessageCircle className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Response Time</h3>
            <p className="text-sm text-gray-600 mb-2">We typically respond within</p>
            <p className="text-sm text-purple-600 font-medium">24 hours</p>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="max-w-4xl mx-auto">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{category.category}</h2>
              <div className="space-y-3">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = categoryIndex * 100 + faqIndex
                  const isOpen = openFAQ === globalIndex
                  return (
                    <div key={faqIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <button
                        onClick={() => toggleFAQ(globalIndex)}
                        className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <div
                        className={`transition-all duration-300 ease-in-out ${
                          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        } overflow-hidden`}
                      >
                        <div className="px-6 pb-4">
                          <p
                            className="text-gray-600 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Need Help Section */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="bg-blue-50 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Still have questions?</h2>
            <p className="text-gray-600 mb-6">
              Our support team is here to help you get the most out of FlowBeatIQ
            </p>
            <a
              href="mailto:support@flowbeatiq.com"
              className="inline-block bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default HelpCenter
